import React, { useCallback, useEffect, useState, memo } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { AnimatedSection } from './AnimatedSection';
export function TestimonialsSection() {
  const testimonials = [{
    name: 'Sarah Johnson',
    role: 'Marketing Director, TechCorp',
    content: 'Working with Turnout for our annual conference was a game-changer. Their attention to detail and creative solutions transformed our event from ordinary to extraordinary. Our attendees are still talking about it months later!',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=687&q=80',
    stars: 5
  }, {
    name: 'Michael Chen',
    role: 'CEO, Innovate Solutions',
    content: 'The team at Turnout went above and beyond for our product launch. They handled every aspect professionally and turned our vision into reality. The event generated significant buzz and exceeded our expectations.',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=687&q=80',
    stars: 5
  }, {
    name: 'Emily Rodriguez',
    role: 'Bride',
    content: "I can't thank Turnout enough for making our wedding day absolutely perfect. From the initial planning to the day-of coordination, everything was handled with such care and professionalism. It was truly the best day of our lives!",
    image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=761&q=80',
    stars: 5
  }, {
    name: 'Robert Thompson',
    role: 'Events Manager, Global Enterprises',
    content: "Turnout consistently delivers excellence. We've partnered with them for our last three corporate retreats, and each time they've raised the bar. Their team's ability to handle logistics while creating memorable experiences is unmatched in the industry.",
    image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=687&q=80',
    stars: 5
  }, {
    name: 'Jennifer Patel',
    role: 'Director, Hope Foundation',
    content: "Our annual fundraising gala needed to impress donors while staying within budget. Turnout understood our mission and delivered an elegant event that helped us exceed our fundraising goals by 40%. They're now our permanent events partner.",
    image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=688&q=80',
    stars: 5
  }];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const nextSlide = useCallback(() => {
    setCurrentIndex(prevIndex => prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1);
  }, [testimonials.length]);
  const prevSlide = useCallback(() => {
    setCurrentIndex(prevIndex => prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1);
  }, [testimonials.length]);
  const goToSlide = index => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    // Resume auto-playing after 5 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };
  // Auto-rotate carousel
  useEffect(() => {
    let interval;
    if (isAutoPlaying) {
      interval = setInterval(() => {
        nextSlide();
      }, 5000); // Change slide every 5 seconds
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isAutoPlaying, nextSlide]);
  // Pause auto-rotation when user interacts with controls
  const handleControlClick = callback => {
    setIsAutoPlaying(false);
    callback();
    // Resume auto-playing after 5 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };
  return <section id="testimonials" className="py-16 bg-gray-50" data-id="element-246">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-247">
        <AnimatedSection data-id="element-248">
          <div className="text-center mb-12" data-id="element-249">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl" data-id="element-250">
              What Our Clients Say
            </h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto" data-id="element-251">
              Don't just take our word for it — hear from some of our satisfied
              clients.
            </p>
          </div>
        </AnimatedSection>
        <AnimatedSection delay={0.2} data-id="element-252">
          <div className="relative max-w-4xl mx-auto" data-id="element-253">
            {/* Carousel container */}
            <div className="overflow-hidden" data-id="element-254">
              <div className="flex transition-transform duration-500 ease-in-out" style={{
              transform: `translateX(-${currentIndex * 100}%)`
            }} data-id="element-255">
                {testimonials.map((testimonial, index) => <div key={index} className="w-full flex-shrink-0 px-4" data-id="element-256">
                    <div className="bg-white rounded-lg shadow-md p-8 flex flex-col h-full" data-id="element-257">
                      <div className="flex items-center mb-4" data-id="element-258">
                        <div className="h-16 w-16 rounded-full overflow-hidden mr-4" data-id="element-259">
                          <img src={testimonial.image} alt={testimonial.name} className="h-full w-full object-cover" data-id="element-260" />
                        </div>
                        <div data-id="element-261">
                          <h3 className="text-lg font-medium text-gray-900" data-id="element-262">
                            {testimonial.name}
                          </h3>
                          <p className="text-sm text-gray-600" data-id="element-263">
                            {testimonial.role}
                          </p>
                        </div>
                      </div>
                      <div className="flex mb-4" data-id="element-264">
                        {[...Array(testimonial.stars)].map((_, i) => <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" data-id="element-265" />)}
                      </div>
                      <p className="text-gray-700 text-lg italic flex-grow" data-id="element-266">
                        "{testimonial.content}"
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Navigation arrows */}
            <button onClick={() => handleControlClick(prevSlide)} className="absolute left-0 top-1/2 -translate-y-1/2 -ml-4 bg-white rounded-full p-2 shadow-md text-gray-600 hover:text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 z-10" aria-label="Previous testimonial" data-id="element-267">
              <ChevronLeft size={24} data-id="element-268" />
            </button>
            <button onClick={() => handleControlClick(nextSlide)} className="absolute right-0 top-1/2 -translate-y-1/2 -mr-4 bg-white rounded-full p-2 shadow-md text-gray-600 hover:text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 z-10" aria-label="Next testimonial" data-id="element-269">
              <ChevronRight size={24} data-id="element-270" />
            </button>
            {/* Indicator dots */}
            <div className="flex justify-center space-x-2 mt-8" data-id="element-271">
              {testimonials.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={`w-3 h-3 rounded-full focus:outline-none ${index === currentIndex ? 'bg-indigo-600' : 'bg-gray-300'}`} aria-label={`Go to testimonial ${index + 1}`} data-id="element-272" />)}
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>;
}